import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getMandateStatusSingle, getMandateStatus } from "../../../api/bseApi";

const POLL_INTERVAL_MS = 60000; // 1 min for PENDING

/**
 * Parse BSE Flag 07 response: Status|Remarks|LastUpdated (pipe-separated)
 */
function parseMandateStatusResponse(rawData) {
  if (!rawData || typeof rawData !== "string") {
    return { status: "UNKNOWN", remarks: "", lastUpdated: "" };
  }
  const parts = rawData.split("|").map((p) => (p || "").trim());
  const upper = rawData.toUpperCase();
  let status = "UNKNOWN";
  if (upper.includes("APPROVED") || upper.includes("ACTIVE")) status = "APPROVED";
  else if (upper.includes("PENDING") || upper.includes("PROCESSING")) status = "PENDING";
  else if (upper.includes("REJECTED") || upper.includes("REJECT")) status = "REJECTED";

  return {
    status,
    remarks: parts[1] || parts[0] || rawData,
    lastUpdated: parts[2] || parts[1] || new Date().toLocaleString(),
  };
}

/**
 * MandateStatusScreen - BSE StAR MF Mandate Status (MFAPI Flag 07)
 * Opens after Mandate Registration redirect (ReturnURL)
 * Receives: MandateID, ClientCode
 */
export default function MandateStatusScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const state = location.state;
  const [context, setContext] = useState(null);
  const [mandateData, setMandateData] = useState(null);
  const [mandatesList, setMandatesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);
  const [manualMandateId, setManualMandateId] = useState("");
  const [manualClientCode, setManualClientCode] = useState("");
  const [showManualEntry, setShowManualEntry] = useState(false);

  const mandateId = state?.mandateId || sessionStorage.getItem("mandateReturnMandateId");
  const clientCode = state?.clientCode || sessionStorage.getItem("mandateReturnClientCode");

  useEffect(() => {
    if (clientCode) {
      sessionStorage.removeItem("mandateReturnClientCode");
      sessionStorage.removeItem("mandateReturnMandateId");
    }
    if (mandateId && clientCode) {
      setContext({ mandateId, clientCode });
      return;
    }
    if (clientCode) {
      setContext({ mandateId: "", clientCode });
      setShowManualEntry(true);
      return;
    }
    if (userId) {
      const fetchUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000/api"}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          if (!ucc || String(ucc).trim() === "" || String(ucc) === "Reject") {
            Swal.fire("Error", "ClientCode (UCC) not available.", "error").then(() => navigate("/user"));
            return;
          }
          setContext({ mandateId: "", clientCode: String(ucc).trim() });
          setShowManualEntry(true);
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setLoading(false);
        }
      };
      fetchUser();
      return;
    }
    Swal.fire("Error", "Missing ClientCode. Open from Mandate Return or Mandate Management.", "error").then(() => navigate("/user"));
    setLoading(false);
  }, [location.state, userId, navigate]);

  const fetchSingleStatus = useCallback(async (mid, cc) => {
    if (!mid || !cc) return null;
    try {
      const res = await getMandateStatusSingle({ mandateId: mid, clientCode: cc });
      if (res.data?.status === 1) {
        return parseMandateStatusResponse(res.data?.data);
      }
      return { status: "ERROR", remarks: res.data?.message || res.data?.error || "API error" };
    } catch (err) {
      return { status: "ERROR", remarks: err.response?.data?.message || err.message || "Fetch failed" };
    }
  }, []);

  const fetchListStatus = useCallback(async (cc) => {
    if (!cc) return;
    try {
      const fromDate = "01/01/2020";
      const d = new Date();
      const toDate = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
      const res = await getMandateStatus({ clientCode: cc, fromDate, toDate });
      if (res.data?.status === 1) {
        const list = res.data?.data || [];
        setMandatesList(Array.isArray(list) ? list : [list]);
      }
    } catch (err) {
      console.warn("Mandate list fetch failed:", err.message);
    }
  }, []);

  useEffect(() => {
    if (!context) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      const mid = context.mandateId || manualMandateId;
      const cc = context.clientCode || manualClientCode;

      if (mid && cc) {
        const result = await fetchSingleStatus(mid, cc);
        if (result) {
          setMandateData(result);
          if (result.status === "PENDING") setPolling(true);
        } else {
          setError("Could not fetch mandate status.");
        }
      } else {
        await fetchListStatus(cc);
      }
      setLoading(false);
    };

    load();
  }, [context, fetchSingleStatus, fetchListStatus]);

  useEffect(() => {
    if (!polling || !mandateData || mandateData.status !== "PENDING") return;
    const timer = setInterval(async () => {
      const mid = context?.mandateId || manualMandateId;
      const cc = context?.clientCode || manualClientCode;
      if (mid && cc) {
        const result = await fetchSingleStatus(mid, cc);
        if (result) {
          setMandateData(result);
          if (result.status !== "PENDING") setPolling(false);
        }
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [polling, mandateData?.status, context, manualMandateId, manualClientCode, fetchSingleStatus]);

  const handleRefresh = async () => {
    const mid = context?.mandateId || manualMandateId;
    const cc = context?.clientCode || manualClientCode;
    if (mid && cc) {
      setLoading(true);
      const result = await fetchSingleStatus(mid, cc);
      if (result) setMandateData(result);
      setLoading(false);
    }
  };

  const handleManualFetch = (e) => {
    e.preventDefault();
    const mid = (manualMandateId || "").trim();
    const cc = (manualClientCode || context?.clientCode || "").trim();
    if (!mid || !cc) {
      Swal.fire("Required", "Enter Mandate ID and Client Code.", "warning");
      return;
    }
    setContext({ mandateId: mid, clientCode: cc });
    setManualMandateId(mid);
    setManualClientCode(cc);
  };

  const effectiveMandateId = context?.mandateId || manualMandateId;
  const effectiveClientCode = context?.clientCode || manualClientCode;
  const hasSingleMandate = !!effectiveMandateId && !!effectiveClientCode;
  const needsManualEntry = !!effectiveClientCode && !effectiveMandateId;

  if (!context && !clientCode) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showManualEntry && !hasSingleMandate && !effectiveClientCode) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <i className="fas fa-exclamation-triangle text-warning fa-3x mb-3" />
              <h5>Client Code Required</h5>
              <p className="text-muted mb-3">Open from Mandate Return or Mandate Management with Client Code.</p>
              <Link to="/user" className="btn btn-primary">Back to Users</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div className="mb-4" style={{ background: "#fff", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", padding: "20px" }}>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">Mandate Status</li>
              </ol>
            </nav>
            <h5>Mandate Status</h5>
            <p className="text-muted mb-0">
              {hasSingleMandate ? (
                <>Mandate ID: <strong>{effectiveMandateId}</strong> | Client: <strong>{effectiveClientCode}</strong></>
              ) : (
                <>Client: <strong>{effectiveClientCode}</strong></>
              )}
            </p>
          </div>

          {needsManualEntry && (
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <h6 className="mb-3" style={{ color: "#19bcb8", fontWeight: "600" }}>Check Mandate Status by ID</h6>
                <form onSubmit={handleManualFetch} className="row g-3">
                  <div className="col-md-4">
                    <input type="text" className="form-control" value={manualMandateId} onChange={(e) => setManualMandateId(e.target.value)} placeholder="Enter Mandate ID" />
                  </div>
                  <div className="col-md-4">
                    <input type="text" className="form-control" value={manualClientCode || effectiveClientCode || ""} onChange={(e) => setManualClientCode(e.target.value)} placeholder="Client Code" readOnly />
                  </div>
                  <div className="col-md-4">
                    <button type="submit" className="btn btn-primary">Fetch Status</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="card shadow-sm">
            <div className="card-body">
              {loading && !mandateData && (
                <div className="text-center py-4">
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Fetching mandate status...
                </div>
              )}
              {error && !mandateData && (
                <div className="alert alert-danger">
                  <i className="fas fa-times-circle me-2" />
                  {error}
                  <div className="mt-3">
                    <button className="btn btn-outline-primary btn-sm me-2" onClick={handleRefresh}>Retry</button>
                    <Link to="/user" className="btn btn-outline-secondary btn-sm">Back to Users</Link>
                  </div>
                </div>
              )}

              {mandateData && (
                <>
                  <h6 className="mb-4" style={{ color: "#19bcb8", fontWeight: "600" }}>Mandate Status</h6>
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="form-label text-muted">Mandate ID</label>
                      <p className="form-control-plaintext fw-bold">{effectiveMandateId}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted">Client Code</label>
                      <p className="form-control-plaintext fw-bold">{effectiveClientCode}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted">Mandate Status</label>
                      <p className="mb-0">
                        {mandateData.status === "APPROVED" && (
                          <span className="badge bg-success fs-6"><i className="fas fa-check-circle me-1" /> APPROVED</span>
                        )}
                        {mandateData.status === "PENDING" && (
                          <span className="badge bg-warning text-dark fs-6"><i className="fas fa-clock me-1" /> PENDING</span>
                        )}
                        {mandateData.status === "REJECTED" && (
                          <span className="badge bg-danger fs-6"><i className="fas fa-times-circle me-1" /> REJECTED</span>
                        )}
                        {mandateData.status === "UNKNOWN" && (
                          <span className="badge bg-secondary fs-6">{mandateData.status}</span>
                        )}
                      </p>
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted">Bank / BSE Remarks</label>
                      <p className="form-control-plaintext small">{mandateData.remarks || "-"}</p>
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted">Last Updated</label>
                      <p className="form-control-plaintext">{mandateData.lastUpdated || new Date().toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                    {mandateData.status === "APPROVED" && (
                      <>
                        <Link to={userId ? `/user/sip-registration/${userId}` : "/user/sip-registration"} state={{ clientCode: effectiveClientCode }} className="btn btn-primary">SIP Registration</Link>
                        <Link to={userId ? `/user/xsip-registration/${userId}` : "/user/xsip-registration"} state={{ clientCode: effectiveClientCode }} className="btn btn-outline-primary">XSIP Registration</Link>
                        <Link to="/user" className="btn btn-outline-secondary">Back to Users</Link>
                      </>
                    )}
                    {mandateData.status === "PENDING" && (
                      <>
                        <button className="btn btn-primary" onClick={handleRefresh} disabled={loading}>
                          {loading ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-sync-alt me-1" />}
                          Refresh
                        </button>
                        <Link to="/user" className="btn btn-outline-secondary">Back to Users</Link>
                        <small className="text-muted align-self-center ms-2">Auto-refresh every {POLL_INTERVAL_MS / 60000} min</small>
                      </>
                    )}
                    {mandateData.status === "REJECTED" && (
                      <>
                        <Link to="/user/mandate-registration" state={{ clientCode: effectiveClientCode }} className="btn btn-warning">
                          <i className="fas fa-redo me-1" /> Re-register Mandate
                        </Link>
                        <Link to="/user" className="btn btn-outline-secondary">Back to Users</Link>
                      </>
                    )}
                    {(mandateData.status === "UNKNOWN" || mandateData.status === "ERROR") && (
                      <>
                        <button className="btn btn-outline-primary" onClick={handleRefresh} disabled={loading}>Refresh</button>
                        <Link to="/user" className="btn btn-outline-secondary">Back to Users</Link>
                      </>
                    )}
                  </div>
                </>
              )}

              {!loading && !mandateData && !error && mandatesList.length > 0 && (
                <>
                  <h6 className="mb-4" style={{ color: "#19bcb8", fontWeight: "600" }}>Mandate List</h6>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Mandate ID</th>
                          <th>Status</th>
                          <th>Bank</th>
                          <th>Account</th>
                          <th>Amount</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mandatesList.map((m, i) => {
                          const mid = m.MandateId || m.mandateId || "";
                          return (
                            <tr key={mid || i}>
                              <td>{mid || "-"}</td>
                              <td>
                                <span className={`badge ${(m.Status || m.status || "").toUpperCase() === "APPROVED" ? "bg-success" : "bg-warning text-dark"}`}>
                                  {m.Status || m.status || "Pending"}
                                </span>
                              </td>
                              <td>{m.BankName || m.bankName || "-"}</td>
                              <td>{m.AccountNo || m.accountNo || "-"}</td>
                              <td>{m.MandateAmount || m.mandateAmount || "-"}</td>
                              <td>
                                <Link to="/user/mandate-status" state={{ mandateId: mid, clientCode: effectiveClientCode }} className="btn btn-sm btn-outline-primary">
                                  View Status
                                </Link>
                                <Link to="/user/mandate-upload" state={{ mandateId: mid, clientCode: effectiveClientCode }} className="btn btn-sm btn-outline-secondary ms-1">
                                  Upload
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {!loading && !mandateData && !error && mandatesList.length === 0 && !hasSingleMandate && (
                <div className="text-center py-4 text-muted">
                  No mandates found. <Link to="/user/mandate-registration" state={{ clientCode: effectiveClientCode }}>Register a mandate</Link>.
                </div>
              )}

              <div className="mt-3">
                <Link to="/user/sip-list" state={{ clientCode: effectiveClientCode }} className="btn btn-outline-primary btn-sm me-2">SIP List (Mandate Shift)</Link>
                <Link to="/user/mandate-registration" state={{ clientCode: effectiveClientCode }} className="btn btn-outline-primary btn-sm me-2">Register New Mandate</Link>
                <Link to="/user/mandate-upload" state={{ clientCode: effectiveClientCode }} className="btn btn-outline-primary btn-sm me-2">Upload Mandate Document</Link>
                <Link to="/user" className="btn btn-outline-secondary btn-sm">Back to Users</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
